import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { logger } from '../../../utils/logger';

const PhotoAttachment = ({ photos, onPhotosChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e?.target?.files || []);
    
    if (files?.length === 0) return;

    // Validate file types and sizes
    const validFiles = files?.filter(file => {
      const isImage = file?.type?.startsWith('image/');
      const isUnder5MB = file?.size <= 5 * 1024 * 1024;
      
      if (!isImage) {
        setError('Only image files are allowed');
        return false;
      }
      if (!isUnder5MB) {
        setError('Images must be under 5MB');
        return false;
      }
      return true;
    });

    if (validFiles?.length === 0) return;

    setUploading(true);
    setError('');

    try {
      // Convert files to base64 for preview (in production, upload to Supabase Storage)
      const photoPromises = validFiles?.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              id: Date.now() + Math.random(),
              name: file.name,
              size: file.size,
              type: file.type,
              preview: e.target.result,
              timestamp: new Date().toISOString()
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const newPhotos = await Promise.all(photoPromises);
      onPhotosChange([...(photos || []), ...newPhotos]);
      logger?.info('Photos attached', { count: newPhotos?.length });
    } catch (err) {
      logger?.error('Photo upload error', err);
      setError('Failed to process images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (photoId) => {
    onPhotosChange((photos || [])?.filter(p => p?.id !== photoId));
  };

  const handleCameraCapture = () => {
    if (fileInputRef?.current) {
      fileInputRef?.current?.setAttribute('capture', 'environment');
      fileInputRef?.current?.click();
    }
  };

  const handleGallerySelect = () => {
    if (fileInputRef?.current) {
      fileInputRef?.current?.removeAttribute('capture');
      fileInputRef?.current?.click();
    }
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h4 className="text-sm md:text-base font-medium text-foreground mb-1">
          Photo Attachments (Optional)
        </h4>
        <p className="text-xs md:text-sm text-muted-foreground">
          Add photos of visible symptoms like rashes, swelling, or bruising
        </p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Upload symptom photos"
      />
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          size="md"
          onClick={handleCameraCapture}
          disabled={uploading}
          iconName="Camera"
          iconPosition="left"
          className="w-full"
        >
          Take Photo
        </Button>
        <Button
          variant="outline"
          size="md"
          onClick={handleGallerySelect}
          disabled={uploading}
          iconName="Image"
          iconPosition="left"
          className="w-full"
        >
          Choose from Gallery
        </Button>
      </div>
      {uploading && (
        <div className="flex items-center justify-center gap-2 p-4 bg-muted rounded-lg">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm text-muted-foreground">Processing images...</span>
        </div>
      )}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="text-error flex-shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-error">{error}</p>
        </div>
      )}
      {photos && photos?.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            {photos?.length} photo{photos?.length !== 1 ? 's' : ''} attached
          </p>
          <div className="grid grid-cols-3 gap-2">
            {photos?.map((photo) => (
              <div key={photo?.id} className="relative group">
                <img
                  src={photo?.preview}
                  alt={photo?.name}
                  className="w-full h-24 object-cover rounded-lg border border-border"
                />
                <button
                  onClick={() => handleRemovePhoto(photo?.id)}
                  className="absolute top-1 right-1 w-6 h-6 bg-error text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove photo"
                >
                  <Icon name="X" size={14} />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 rounded-b-lg truncate">
                  {photo?.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <Icon name="Info" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-800">
          <p className="font-medium mb-1">Privacy Note:</p>
          <p>Photos are stored securely and only visible to you and authorized caregivers. Maximum 5MB per image.</p>
        </div>
      </div>
    </div>
  );
};

export default PhotoAttachment;